<template>
  <div
    class="trainer-ranges-comparison trrcmp"
    data-tst="ranges_comparison"
  >
    <div class="trrcmp_top">
      <div
        v-if="hasTitlePlayers"
        class="trrcmp_players"
      >
        <div
          class="trrcmp_players_player trrcmp_players_playerLeft"
          style="gap: 0.25rem"
        >
          {{ $t(`players.${playerLeft.position}`) }}
          <i
            v-if="playerLeft.is_hero"
            class="mdi mdi-crown"
          ></i>
        </div>
        <div class="trrcmp_players_player trrcmp_players_playerRight">
          {{ $t(`players.${playerRight.position}`) }}
          <i
            v-if="playerRight.is_hero"
            class="mdi mdi-crown"
          ></i>
        </div>
      </div>
      <CompareChartBox
        v-for="item in staticRows"
        :key="item.id"
        class="trrcmp_item"
        :style="`
        --weight: 500;
        --weight-legend: 500;
        ${
          item.isNA || skeleton
            ? '--clr-equal:var(--clr-grey-2);'
            : '--clr-equal:var(--clr-primary);'
        }
        ${item.style}
      `"
        :isCentered="styleEqBuckets === 'two'"
        :hasColoredLegend="true"
        :skeleton="skeleton"
        :pLeft="item.pLeft"
        :pRight="item.pRight"
        :data-tst="`trrcmp_item_${item.id}`"
      >
        <template #legend_left>
          {{ item.isNA ? 'N/A' : numFixed(item.fn(item.playerLeft), item.decimal) }}{{ item.unit }}
          <div
            v-if="item.playerLeft_note && !(item.isNA || skeleton)"
            :id="item.id"
            class="trrcmp_avg-note"
            :class="item.note_css_class"
          >
            {{ numFixed(item.fn_note(item.playerLeft_note), item.decimal)
            }}{{ item.unit_note || '' }}
          </div>
        </template>
        <template #legend_center>
          <div
            class="text-nowrap truncate clr-grey"
            style="max-width: 10rem"
            :title="item.title"
          >
            {{ item.title }}
          </div>
          <GuideTooltip
            v-if="hasGuides && item.guide"
            :guide="item.guide"
            style="position: absolute; right: -1rem; top: 0px; --csst-min-after-width: 250%"
          />
        </template>
        <template #legend_right>
          <div
            v-if="item.playerRight_note && !(item.isNA || skeleton)"
            :id="item.id"
            class="trrcmp_avg-note"
            :class="item.note_css_class"
          >
            {{ numFixed(item.fn_note(item.playerRight_note), item.decimal)
            }}{{ item.unit_note || '' }}
          </div>

          {{ item.isNA ? 'N/A' : numFixed(item.fn(item.playerRight), item.decimal) }}{{ item.unit }}
        </template>
      </CompareChartBox>

      <slot name="message" />

      <StudyEqBucketsSwitcherNew
        v-show="showFlopContent"
        :hasHelp="hasHelp"
        :showEquityBucketsOnly="isPreflop"
        class="mt-4 mb-2 z-index-1"
      />
    </div>

    <div
      class="trrcmp_items-container"
      :class="{ 'no-events': readOnly || skeleton }"
      @mouseleave="handleContMouseleave"
      @mouseenter="handleContMouseenter"
    >
      <slot
        :name="`message_cat_${!visibleCategories.items.length ? 'empty' : 'full'}`"
        :item="visibleCategories"
      />

      <CompareChartBox
        v-for="item in showFlopContent ? visibleCategories.items : []"
        :key="item.id"
        class="trrcmp_item gw_btn_hover_back cursor-pointer"
        :class="{
          gw_btn_hover_back_active: filterCategories.some(i => i.name === item.name),
        }"
        :style="`
        --weight: 500;
        --weight-legend: 500;
         ${
           visibleCategories.hand === item.name
             ? '--clr-legend-center:var(--clr-trrcmp-item-active)'
             : '--clr-legend-center: var(--clr-trrcmp-item-inactive)'
         }
        ${
          item.isNA || skeleton ? '--clr-equal:var(--clr-grey-2)' : '--clr-equal:var(--clr-primary)'
        }
      `"
        :isCentered="styleEqBuckets === 'two'"
        :hasColoredLegend="true"
        :skeleton="skeleton"
        :pLeft="item.pLeft"
        :pRight="item.pRight"
        :data-tst="`trrcmp_item_${item.id}`"
        @mouseenter="handleMouseenter(item)"
        @click="handleClick(item)"
      >
        <template #legend_left>
          {{ item.isNA ? 'N/A' : numFixed(item.fn(item.playerLeft), item.decimal) }}{{ item.unit }}
        </template>
        <template #legend_center>
          <div
            class="text-nowrap truncate"
            style="max-width: 10rem"
            :title="item.title"
          >
            {{ item.title }}
          </div>
        </template>
        <template #legend_right>
          {{ item.isNA ? 'N/A' : numFixed(item.fn(item.playerRight), item.decimal) }}{{ item.unit }}
        </template>
      </CompareChartBox>
    </div>
  </div>
</template>

<script>
import CompareChartBox from '@/components/Charts/CompareChartBox/CompareChartBox'
import GuideTooltip from '@/components/Study/Switchers/GuideTooltip'
import { throttle } from 'lodash'
import { getPercentageOfMax } from '@/helpers/helpers'
import {
  unitDecimals,
  getCategoriesHandIndex,
  getCategoriesDrawsIndex,
  getCategoriesEqBucketsIndex,
  getCategoriesEqBucketsAdvancedIndex,
} from '@/helpers/constants'
import StudyEqBucketsSwitcherNew from '@/components/Switchers/StudyEqBucketsSwitcherNew'
import format_mix from '@/mixins/format_mix'

export default {
  name: 'TrainerRangesComparison',
  components: {
    StudyEqBucketsSwitcherNew,
    CompareChartBox,
    GuideTooltip,
  },
  mixins: [format_mix],
  props: {
    catHands: { default: () => [] },
    catDraws: { default: () => [] },
    catEqBuckets: { default: () => [] },
    catEqBucketsAdvanced: { default: () => [] },
    filterCategories: { default: () => [] },
    filterCombos: Object,
    handInfo: Object,
    hasGuides: Boolean,
    hasHelp: { type: Boolean, default: true },
    hasTitlePlayers: Boolean,
    playerLeft: Object,
    playerRight: Object,
    playerLeftKey: { required: true, type: String },
    playerRightKey: { required: true, type: String },
    isPreflop: { type: Boolean, required: true },
    readOnly: Boolean,
    showFlopContent: { type: Boolean, required: true },
    skeleton: Boolean,
    staticCustomRows: { default: () => [] },
    staticHiddenRows: { default: () => [] },
    staticRowsOrder: { default: () => [] },
  },
  emits: ['cat:mouseenter', 'cat:mouseleave', 'cat:click'],
  data() {
    return {
      mouseover: false,
    }
  },
  methods: {
    handleMouseenter: throttle(function (item) {
      if (this.readOnly) return
      if (!this.mouseover) return
      this.$emit('cat:mouseenter', item)
    }, 40),
    handleContMouseenter() {
      this.mouseover = true
    },
    handleContMouseleave() {
      if (this.readOnly) return
      this.mouseover = false
      this.$emit('cat:mouseleave')
    },
    handleClick(item) {
      if (this.readOnly) return
      this.$emit('cat:click', item)
    },
    getPercent(l, r, player) {
      const max = l + r
      const res = getPercentageOfMax({ max, value: player })
      return res
    },
    getVisibleCategories({ categories, category_id = 'draw_categories' }) {
      if (this.filterCombos?.actions?.length) {
        const total_range = this.filterCombos?.actions.reduce(
          (a, b) => a + b.total_combos_default,
          0,
        )
        categories = categories.map(category => {
          const action_total_category_range = this.filterCombos?.actions.reduce((acc, item) => {
            const actionCategory =
              item.hand_categories?.find(i => i.name === category.name) ||
              item.draw_categories?.find(i => i.name === category.name) ||
              item.equity_buckets?.find(i => i.name === category.name) ||
              item.equity_buckets_advanced?.find(i => i.name === category.name) ||
              null

            return acc + actionCategory?.total_combos || 0
          }, 0)

          const occurence = action_total_category_range / (total_range || 1)
          const total_occurrence = occurence > 1 ? 1 : occurence
          const r = {
            ...category,
            players: [
              {
                ...category.players[0], // is hero
                total_occurrence,
              },
              ...category.players.slice(1),
            ],
            // TODO: remove after 3way release
            hero: {
              ...category.hero,
              total_occurrence,
            },
          }
          return r
        })
      }

      const fn = val => val * 100
      let res = categories.map(c => {
        const playerLeft = this.showFlopContent
          ? c.players[this.playerLeftKey]?.total_occurrence || 0
          : 0
        const playerRight = this.showFlopContent
          ? c.players[this.playerRightKey]?.total_occurrence || 0
          : 0

        const pLeft = this.getPercent(playerLeft, playerRight, playerLeft)
        const pRight = this.getPercent(playerLeft, playerRight, playerRight)
        const r = {
          title: this.$t(`browse.filter_rules.${c.name}`),
          id: c.name,
          name: c.name,
          isNA: !this.showFlopContent,
          unit: this.showFlopContent ? '%' : '',
          decimal: 1,
          playerLeft,
          playerRight,
          pLeft, // playerLeft
          pRight, // playerRight
          fn,
          category_id,
          display_order: c.display_order,
          category: c,
        }
        return r
      })
      res = res.filter(i => i.playerLeft || i.playerRight) // remove empty
      res = res.sort((a, b) => (a.display_order > b.display_order ? 1 : -1))
      return res
    },
  },
  computed: {
    unOptions() {
      const res = [
        {
          icon: 'mdi-layers-outline',
          payload: 'eq_buckets_simple',
        },
        {
          icon: 'mdi-layers-triple-outline',
          payload: 'eq_buckets_advanced',
        },
        {
          payload: 'hands',
          icon_class: 'icon_hands',
        },
        {
          payload: 'draws',
          icon_class: 'icon_draws',
        },
      ].map(i => ({
        ...i,
        text: this.$t(`browse.${i.payload}`),
      }))
      return res
    },
    hands() {
      const res = {
        id: 'hands',
        items: this.getVisibleCategories({
          category_id: 'hand_categories',
          categories: this.catHands,
        }),
        hand: getCategoriesHandIndex()[this.handInfo?.hand_category],
      }
      return res
    },
    draws() {
      const res = {
        id: 'draws',
        items: this.getVisibleCategories({
          category_id: 'draw_categories',
          categories: this.catDraws,
        }),
        hand: getCategoriesDrawsIndex()[this.handInfo?.draw_category],
      }
      return res
    },
    bucketsSimple() {
      const res = {
        id: 'equity_buckets',
        items: this.getVisibleCategories({
          category_id: 'equity_buckets',
          categories: this.catEqBuckets,
        }),
        hand: getCategoriesEqBucketsIndex()[this.handInfo?.equity_buckets],
      }
      return res
    },
    bucketsAdvanced() {
      const res = {
        id: 'equity_buckets_advanced',
        items: this.getVisibleCategories({
          category_id: 'equity_buckets_advanced',
          categories: this.catEqBucketsAdvanced,
        }),
        hand: getCategoriesEqBucketsAdvancedIndex()[this.handInfo?.equity_buckets_advanced],
      }
      return res
    },
    visibleCategories() {
      switch (this.$store.getters.sw_range_compare) {
        case 'swv_eq_buckets_simple':
          return this.bucketsSimple
        case 'swv_eq_buckets_advanced':
          return this.bucketsAdvanced
        case 'swv_hands':
          return this.hands
        case 'swv_draws':
          return this.draws
        default:
          return { items: [], hand: '' }
      }
    },
    staticRows() {
      const filterActive = this.filterCombos && this.filterCombos?.status !== 'inactive'
      const playerLeftTotalRange = filterActive
        ? this.playerLeft.total_range_filtered
        : this.playerLeft.total_combos
      const playerRightTotalRange = filterActive
        ? this.playerRight.total_range_filtered
        : this.playerRight.total_combos
      const combos = {
        id: 'combos',
        title: this.$t('guides.combos'),
        decimal: unitDecimals.combos,
        unit_note: '%',
        note_css_class: 'trrcmp_avg-note',
        fn_note: val => val * 100,
        hasCombosNote: true,
        guide: 'ra_compare_combos',
        name: 'total_combos',
        playerLeftValue: playerLeftTotalRange,
        playerRightValue: playerRightTotalRange,
      }
      const ev = {
        id: 'ev',
        title: this.$t('common.ev'),
        decimal: unitDecimals.ev,
        unit_note: '%',
        fn_note: val => val * 100,
        hasEvNote: true,
        guide: 'ra_compare_ev',
        name: 'total_ev',
        playerLeftValue: this.playerLeft?.total_ev,
        playerRightValue: this.playerRight?.total_ev,
      }
      const eq = {
        id: 'eq',
        fn: val => val * 100,
        guide: 'ra_compare_eq',
        decimal: unitDecimals.eq,
        isNA: !this.showFlopContent,
        name: 'total_eq',
        title: this.$t('common.equity'),
        unit: this.showFlopContent ? '%' : '',
        playerLeftValue: this.playerLeft?.total_eq,
        playerRightValue: this.playerRight?.total_eq,
      }
      const eqr = {
        id: 'eqr',
        fn: val => val * 100,
        guide: 'ra_compare_eqr',
        decimal: unitDecimals.eqr,
        isNA: !this.showFlopContent,
        name: 'total_eqr',
        title: this.$t('common.eqr'),
        unit: this.showFlopContent ? '%' : '',
        playerLeftValue: this.playerLeft?.total_eqr,
        playerRightValue: this.playerRight?.total_eqr,
      }

      let res = [combos, ev, eq, eqr, ...this.staticCustomRows].filter(
        i => !this.staticHiddenRows.includes(i.id),
      )
      res = res.sort(
        (a, b) => this.staticRowsOrder.indexOf(a.id) - this.staticRowsOrder.indexOf(b.id),
      )

      res = res.map(item => {
        const playerLeft = item.isNA ? 0 : item.playerLeftValue || 0
        const playerRight = item.isNA ? 0 : item.playerRightValue || 0

        const pLeft = this.getPercent(playerLeft, playerRight, playerLeft)
        const pRight = this.getPercent(playerLeft, playerRight, playerRight)
        const dfn = val => val
        const fn = item.fn ? item.fn : dfn

        let style = item.style || ''
        let playerLeft_note = ''
        let playerRight_note = ''

        if (item.hasCombosNote && filterActive) {
          style = '--clr-back-winn:var(--clr-winning-streak);'
          playerLeft_note =
            this.playerLeft.is_hero ||
            [
              'without_actions', //
              'with_actions',
            ].includes(this.filterCombos?.status)
              ? (playerLeft || 0) / (this.playerLeft.total_combos || 1)
              : ''

          playerRight_note =
            this.playerRight.is_hero ||
            [
              'without_actions', //
              'with_actions',
            ].includes(this.filterCombos?.status)
              ? (playerRight || 0) / (this.playerRight.total_combos || 1)
              : ''
        } else if (item.hasEvNote) {
          playerLeft_note = pLeft
          playerRight_note = pRight
        }

        const r = {
          ...item,
          unit: item.unit || '',
          unit_note: item.unit_note || '',
          playerLeft,
          playerRight,
          playerLeft_note: item.playerLeft_note ?? playerLeft_note,
          playerRight_note: item.playerRight_note ?? playerRight_note,
          pLeft,
          pRight,
          fn,
          style,
        }
        return r
      })

      return res
    },

    styleEqBuckets() {
      const { mergedUserSettings } = this.$store.getters
      return mergedUserSettings.styleEqBuckets
    },
  },
}
</script>
<style lang="scss" scoped>
.trrcmp {
  width: 100%;
  --text-size: 1rem;
  --player-size: 0.875rem;
  --position-top: relative;
  --clr-top-back: transparent;
  --top-padding: 0;

  // --clr-trrcmp-legend-center: var(--clr-white-0);
  --clr-trrcmp-item-active: var(--clr-white-0);
  --clr-trrcmp-item-inactive: var(--clr-white-0);

  font-size: var(--text-size);
  &_top {
    position: var(--position-top);
    z-index: 2;
    top: 0;
    background: var(--clr-top-back);
    padding: var(--top-padding);
  }
  &_players {
    display: flex;
    width: 100%;
    justify-content: space-between;
    font-size: var(--player-size);
    --order-playerLeft: 1;
    --order-playerRight: 2;
    &_player {
      display: flex;
      gap: 0.25rem;
      font-weight: 700;
      color: var(--clr-grey-2);
    }
    &_playerLeft {
      order: var(--order-playerLeft);
    }
    &_playerRight {
      order: var(--order-playerRight);
    }
  }
  &_items-container {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    // gap: ;
  }
  &_avg-note {
    color: var(--clr-grey-3);
    text-transform: lowercase;
    font-size: 0.7rem;
    font-weight: 700;
    line-height: 1;
    // margin: auto 0.25rem 0.25rem 0.25rem;
    margin: auto 0.25rem 0.3rem 0.25rem;
    // margin: 1rem 0.25rem 0 0.25rem;
    // &_on-top {
    //   margin: 0;
    //   position: absolute;
    //   top: -0.8rem;
    //   height: 0;
    // }
  }
  &_item {
    min-height: 2.75rem;
    display: flex;
    align-items: center;
    --btn-back-inset: 0 auto auto -0.25rem;
    --btn-back-width: calc(100% + 0.5rem);
  }
}
</style>
